import {
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Button, Col, Drawer, Modal, Row, Spin, Typography, message } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQueries from '../../api/useAPIQueries';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import InterviewScriptTemplateForm, {
  InterviewScriptTemplateFormValues,
} from '../../components/InterviewScriptTemplateForm';
import PageTitle from '../../components/PageTitle';
import { fixUnorderedListInQuill } from '../../components/RichEditor/RichEditor';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { InterviewScriptTemplateDetail } from '../../types';
import styles from './EditInterviewScriptTemplate.module.css';

const EditInterviewScriptTemplate = () => {
  const { t } = useTranslation();

  const { tenant, interview_script_template_id } = useTypedParams({
    tenant: String,
    interview_script_template_id: Number,
  });
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [setIsDirty] = useDiscardChangesModal(form);
  const navigate = useTypedNavigate();

  const config = useAPIQuery('/configs/tenant-configurations/');

  const interviewScriptTemplate = useAPIQuery(
    `/interview-script-templates/${interview_script_template_id}/`
  );

  const emailCommunicationTemplate = useAPIQuery(
    `/email-communication-templates/${interviewScriptTemplate.data?.email_communication_template}/`,
    {
      enabled: !!interviewScriptTemplate.data?.email_communication_template,
    }
  );

  const attachments = useAPIQueries(
    emailCommunicationTemplate.data?.attachments.map((attachmentId) => [
      `/attachments/${attachmentId}/`,
      {},
    ]) || []
  );

  const stages = useAPIQuery([
    '/stages/',
    {
      params: {
        interview_script_template: Number(interview_script_template_id),
      },
    },
  ]);

  const editMutation = useAPIMutation(
    async (
      {
        name,
        type,
        content,
        emailCommunicationContent,
        attachments,
      }: InterviewScriptTemplateFormValues,
      api
    ) => {
      const configs = await api.get('/configs/tenant-configurations/');
      let emailCommunicationTemplateId = interviewScriptTemplate.data?.email_communication_template;

      if (type.includes('PRACTICAL_TECHNICAL_CASE')) {
        const attachmentIds: number[] = [];

        for (const attachment of attachments) {
          const attachmentId = parseInt(attachment.uid, 10);

          if (emailCommunicationTemplate.data?.attachments.includes(attachmentId)) {
            attachmentIds.push(attachmentId);
          } else {
            const attachmentFormData = new FormData();
            attachmentFormData.append('filename', attachment.name);
            attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
            attachmentFormData.append('mimetype', attachment.type as string);
            const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            attachmentIds.push(attachmentResponse.data.id);
          }
        }

        if (!interviewScriptTemplate.data?.email_communication_template) {
          // TODO - VER COMO VAI TRADUZIR ISSO AQUI. O recrutador pode ta com o sistema em pt e querer enviar email em en
          emailCommunicationTemplateId = (
            await api.post('/email-communication-templates/', {
              name: `${name} - Modelo Case Prático ou Técnico`,
              from_name: configs.data.companyFromName,
              message: emailCommunicationContent,
              subject: 'Case prático ou técnico',
              type: 'PRACTICAL_TECHNICAL_CASE',
              attachments: attachmentIds,
              individual: false,
            })
          ).data.id;
        } else {
          await api.patch(
            `/email-communication-templates/${interviewScriptTemplate.data.email_communication_template}/`,
            {
              message: emailCommunicationContent,
              attachments: attachmentIds,
            }
          );
        }
      }

      await api.patch(`/interview-script-templates/${interview_script_template_id}/`, {
        name,
        type,
        content,
        email_communication_template: emailCommunicationTemplateId,
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success(t('Roteiro atualizado com sucesso.'));
        navigate({
          pathname: '/:tenant/settings/interviewScriptTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/interview-script-templates/');
        await queryClient.invalidateQueries(
          `/interview-script-templates/${interview_script_template_id}/`
        );
        if (emailCommunicationTemplate.data?.id) {
          await queryClient.invalidateQueries(
            `/email-communication-templates/${emailCommunicationTemplate.data?.id}/`
          );
        }
      },
    }
  );

  const deleteMutation = useAPIMutation(
    async (interviewScriptTemplate: InterviewScriptTemplateDetail, api) => {
      await api.delete(`/interview-script-templates/${interviewScriptTemplate.id}/`);
    },
    {
      onSuccess: async () => {
        message.success(t('Roteiro deletado'));
        navigate({
          pathname: '/:tenant/settings/interviewScriptTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/interview-script-templates/');
      },
    }
  );

  const onClose = () => {
    navigate({
      pathname: '/:tenant/settings/interviewScriptTemplates/',
      params: { tenant },
      search: searchParams,
    });
  };

  const isMutationLoading = editMutation.isLoading || deleteMutation.isLoading;

  return (
    <ErrorResult error={interviewScriptTemplate.error || stages.error}>
      <Drawer
        open
        size="large"
        className={styles.drawer}
        closable={false}
        destroyOnClose
        maskClosable
        onClose={onClose}
        title={
          <PageTitle
            title={interviewScriptTemplate.data?.name || '...'}
            level={2}
            extra={[
              <Button key="close" type="text" onClick={onClose} shape="circle">
                <CloseOutlined />
              </Button>,
            ]}
          />
        }
        footer={
          <Row gutter={[16, 16]}>
            <Col>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={isMutationLoading}
                onClick={() => form.submit()}
              >
                {' '}
                <Trans>Salvar</Trans>
              </Button>
            </Col>
            <Col>
              <Button loading={isMutationLoading} onClick={onClose}>
                <Trans>Fechar</Trans>
              </Button>
            </Col>
            <Col flex={1} />
            <Col>
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={isMutationLoading}
                onClick={() => {
                  return new Promise((resolve) => {
                    const modal = Modal.confirm({});
                    modal.update({
                      title: 'Atenção!',
                      icon: <ExclamationCircleOutlined />,
                      content: (
                        <Typography.Text>
                          <Trans>Ao confirmar, o roteiro</Trans>{' '}
                          <strong>{interviewScriptTemplate.data?.name}</strong>
                          <Trans>
                            será excluído e não poderá ser recuperado. Você tem certeza que deseja
                            excluir?
                          </Trans>
                        </Typography.Text>
                      ),
                      cancelButtonProps: {
                        onClick: () => {
                          modal.destroy();
                          resolve(true);
                        },
                      },
                      okText: t('Sim, excluir roteiro'),
                      okButtonProps: {
                        onClick: () => {
                          setIsDirty(false);
                          if ((stages.data?.count ?? 0) > 0) {
                            return Modal.warning({
                              title: t('Atenção!'),
                              content: t(
                                'Este template está vinculado a uma vaga e por isso não pode ser excluido. Para remover, primeiro desvincule-o da vaga.'
                              ),
                            });
                          }
                          if (interviewScriptTemplate.data) {
                            deleteMutation.mutate(interviewScriptTemplate.data);
                          }
                          modal.destroy();
                          resolve(false);
                        },
                      },
                      closable: true,
                      maskClosable: true,
                    });
                  });
                }}
              >
                {' '}
                <Trans>Deletar</Trans>
              </Button>
            </Col>
          </Row>
        }
      >
        <Spin spinning={interviewScriptTemplate.isLoading || stages.isLoading}>
          <Form<InterviewScriptTemplateFormValues>
            form={form}
            layout="vertical"
            onFinish={(variables) => {
              editMutation.mutate({
                ...variables,
                content: fixUnorderedListInQuill(variables.content),
                emailCommunicationContent: fixUnorderedListInQuill(
                  variables.emailCommunicationContent
                ),
              });
            }}
            initialValues={{
              name: interviewScriptTemplate.data?.name,
              type: interviewScriptTemplate.data?.type.includes('PRACTICAL_TECHNICAL_CASE')
                ? ['PRACTICAL_TECHNICAL_CASE']
                : interviewScriptTemplate.data?.type.filter(
                    (value) => value !== 'SCREENING_INTERVIEW'
                  ),
              content: interviewScriptTemplate.data?.content,
              emailCommunicationContent: emailCommunicationTemplate.data?.message,
              attachments: attachments.data?.map((attachment) => ({
                uid: attachment.id,
                name: attachment.filename,
                status: 'done',
                url: attachment.file,
                type: attachment.mimetype,
                fileName: attachment.filename,
              })),
            }}
          >
            <InterviewScriptTemplateForm
              enableChangeLanguage={config.data?.t007_enableChangeLanguage || false}
            />
          </Form>
        </Spin>
      </Drawer>
    </ErrorResult>
  );
};

export default withRouteChange()(EditInterviewScriptTemplate);
